import React from 'react';
import ReactDOM from 'react-dom';

import App from './App';
import CitySelection from './components/CitySelection';
import TargetMenu from './components/TargetMenu';
import EmoteWheel from './components/EmoteWheel';
import ProfessionsMenu from './features/professionsMenu';
import CraftingSession from './features/craftingSession';
import FishingMinigame from './features/fishingMinigame';
import CraftMenu from './features/craftMenu';
import StationSpawner from './features/stationSpawner';
import Toast from './features/toast';
import TargetHint from './features/targetHint';
import PlacementHud from './features/placementHud';
import PlacementGizmo from './features/placementGizmo';
import WorldEditor from './features/worldEditor';
import ProfMenu from './features/profMenu';
import VoiceHud from './features/voiceHud';
import MapHideOverlay from './features/mapHideOverlay';
import DoorKeys from './components/DoorKeys';
import AdminBroadcast from './features/adminBroadcast';
import AdminPanel from './features/adminPanel';
import PersonalDebug from './features/personalDebug';
import Vitals from './features/vitals';
import DownedScreen from './features/downedScreen';
import EscMenu from './features/escMenu';
import Trade from './features/trade';
import CaptureConsent from './features/captureConsent';
import DoorLockConfig from './components/DoorLockConfig';
import DungeonLootConfig from './components/DungeonLootConfig';
import DungeonManager from './components/DungeonManager';
import DungeonSearchProgress from './components/DungeonSearchProgress';
import WildlifeZoneManager from './components/WildlifeZoneManager';
import ZonePreviewOverlay from './components/ZonePreviewOverlay';
import ZoneTypePicker from './components/ZoneTypePicker';
import HouseAdminConfig from './components/HouseAdminConfig';
import HouseBuyPrompt from './components/HouseBuyPrompt';
import HousingManage from './components/HousingManage';
import HomeDecor from './components/HomeDecor';
import HorseStable from './components/HorseStable';
import DoorCodePrompt from './components/DoorCodePrompt';
import DoorLocksList from './components/DoorLocksList';
import AfkChallenge from './components/AfkChallenge';
import FinePrompt from './components/FinePrompt';
import FineResponse from './components/FineResponse';
import Mailbox from './features/mailbox';
import NoticeBoard from './features/noticeBoard';
import PublicWork from './features/publicWork';
import Depot from './features/depot';
import Contracts from './features/contracts';
import PapersMenu from './features/papers';
import PapersShow from './features/papersShow';
import ItemGiver from './components/ItemGiver';
import { mountDevPreviewPanel } from './devPreviewPanel';

import { store } from './redux/store';
import { Provider } from 'react-redux';

import { Widgets } from './utils/Widgets';
import { initPalette } from './theme/palette';
import { initUiScale, stepUserScale } from './theme/uiScale';

import './main.scss';

// Paint the stored theme palette on :root before any panel mounts, so every
// independently-mounted panel (see the ReactDOM.render calls below) renders
// with the right colors from the start, not just after the esc menu opens.
initPalette();
// Same reasoning, for the resolution-matched UI scale (see theme/uiScale.ts's
// doc comment) - applied before any panel mounts so nothing has to redraw at
// the wrong size for even one frame.
initUiScale();

// Ctrl+wheel zoom, from anywhere - the escape hatch for a panel that renders
// too big to reach its own options tab (e.g. the esc menu overflowing at an
// unusual resolution/DPI combo): this works without ever needing to click
// into that panel first. `passive: false` + preventDefault so it doesn't
// also scroll whatever's under the cursor at the same time.
window.addEventListener('wheel', (e) => {
  if (!e.ctrlKey) return;
  e.preventDefault();
  stepUserScale(e.deltaY < 0 ? 1 : -1);
}, { passive: false });

const DEV_PREVIEW = !window.skyrimPlatform;

if (!window.skyrimPlatform) {
  window.skyrimPlatform = {};
  window.needToScroll = true;
}

if (!window.skyrimPlatform.widgets) {
  window.skyrimPlatform.widgets = new Widgets([]);
}

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <App elem={window.skyrimPlatform.widgets.get()} />
    </Provider>
  </React.StrictMode>,
  document.getElementById('root')
);

// Called from skymp5-functions-lib, chatProperty.ts
window.scrollToLastMessage = () => {
  const _list = document.querySelector('#chat > .chat-main > .list > .chat-list');
  if (_list != null && window.needToScroll) { _list.scrollTop = _list.offsetHeight * _list.offsetHeight; }
};

window.playSound = (name) => {
  (new Audio(require('./sound/' + name).default)).play();
};

if (window.skyrimPlatform?.sendMessage) {
  window.skyrimPlatform.sendMessage('front-loaded');
}

// Mount CitySelection in its own DOM node (always active, manages its own visibility)
const citySelectionRoot = document.getElementById('city-selection-root');
if (citySelectionRoot) {
  ReactDOM.render(<CitySelection />, citySelectionRoot);
}

// Mount TargetMenu in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection: it must render regardless
// of the main App's isLoggined/widgets state.
const targetMenuRoot = document.getElementById('target-menu-root');
if (targetMenuRoot) {
  ReactDOM.render(<TargetMenu />, targetMenuRoot);
}

// Mount EmoteWheel in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu.
const emoteWheelRoot = document.getElementById('emote-wheel-root');
if (emoteWheelRoot) {
  ReactDOM.render(<EmoteWheel />, emoteWheelRoot);
}

// Mount ProfessionsMenu in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const professionsMenuRoot = document.getElementById('profession-menu-root');
if (professionsMenuRoot) {
  ReactDOM.render(<ProfessionsMenu />, professionsMenuRoot);
}

// Mount CraftingSession in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const craftMinigameRoot = document.getElementById('craft-minigame-root');
if (craftMinigameRoot) {
  ReactDOM.render(<CraftingSession />, craftMinigameRoot);
}

// Mount FishingMinigame in its own DOM node - server-driven like the craft
// one, but its own component: the mechanic diverges (the zone drifts, because
// a fish runs) even though the shell deliberately does not.
const fishingMinigameRoot = document.getElementById('fishing-minigame-root');
if (fishingMinigameRoot) {
  ReactDOM.render(<FishingMinigame />, fishingMinigameRoot);
}

// Mount CraftMenu in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const craftMenuRoot = document.getElementById('craft-menu-root');
if (craftMenuRoot) {
  ReactDOM.render(<CraftMenu />, craftMenuRoot);
}

// Mount Toast in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const toastRoot = document.getElementById('toast-root');
if (toastRoot) {
  ReactDOM.render(<Toast />, toastRoot);
}

// Key legend while placing an object (admin tool and house furniture)
const voiceHudRoot = document.getElementById('voice-hud-root');
if (voiceHudRoot) {
  ReactDOM.render(<VoiceHud />, voiceHudRoot);
}

const targetHintRoot = document.getElementById('target-hint-root');
if (targetHintRoot) {
  ReactDOM.render(<TargetHint />, targetHintRoot);
}

const profMenuRoot = document.getElementById('prof-menu-root');
if (profMenuRoot) {
  ReactDOM.render(<ProfMenu />, profMenuRoot);
}

const worldEditorRoot = document.getElementById('world-editor-root');
if (worldEditorRoot) {
  ReactDOM.render(<WorldEditor />, worldEditorRoot);
}

const placementGizmoRoot = document.getElementById('placement-gizmo-root');
if (placementGizmoRoot) {
  ReactDOM.render(<PlacementGizmo />, placementGizmoRoot);
}

const placementHudRoot = document.getElementById('placement-hud-root');
if (placementHudRoot) {
  ReactDOM.render(<PlacementHud />, placementHudRoot);
}

// Outline and question of the tool that takes an object out of the map (admin)
const mapHideRoot = document.getElementById('map-hide-root');
if (mapHideRoot) {
  ReactDOM.render(<MapHideOverlay />, mapHideRoot);
}

// Panel of a verrou (a door locked with keys, no building behind it)
const doorKeysRoot = document.getElementById('door-keys-root');
if (doorKeysRoot) {
  ReactDOM.render(<DoorKeys />, doorKeysRoot);
}

// Mount AdminBroadcast in its own DOM node - same reasoning as Toast, always
// active regardless of which panel (if any) is currently open.
const adminBroadcastRoot = document.getElementById('admin-broadcast-root');
if (adminBroadcastRoot) {
  ReactDOM.render(<AdminBroadcast />, adminBroadcastRoot);
}

// Mount StationSpawner in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const stationSpawnerRoot = document.getElementById('station-spawner-root');
if (stationSpawnerRoot) {
  ReactDOM.render(<StationSpawner />, stationSpawnerRoot);
}

// Mount AdminPanel in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const adminPanelRoot = document.getElementById('admin-panel-root');
if (adminPanelRoot) {
  ReactDOM.render(<AdminPanel />, adminPanelRoot);
}

// Mount the personal debug panel (/debug in chat) in its own DOM node, same reasoning as AdminPanel.
const personalDebugRoot = document.getElementById('personal-debug-root');
if (personalDebugRoot) {
  ReactDOM.render(<PersonalDebug />, personalDebugRoot);
}

// Mount Trade in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const tradeRoot = document.getElementById('trade-root');
if (tradeRoot) {
  ReactDOM.render(<Trade />, tradeRoot);
}

// Mount Vitals in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const vitalsRoot = document.getElementById('vitals-root');
if (vitalsRoot) {
  ReactDOM.render(<Vitals />, vitalsRoot);
}

// Mount DownedScreen in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const downedScreenRoot = document.getElementById('downed-screen-root');
if (downedScreenRoot) {
  ReactDOM.render(<DownedScreen />, downedScreenRoot);
}

// Mount EscMenu in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const escMenuRoot = document.getElementById('esc-menu-root');
if (escMenuRoot) {
  ReactDOM.render(<EscMenu />, escMenuRoot);
}

// Mount CaptureConsent in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const captureConsentRoot = document.getElementById('capture-consent-root');
if (captureConsentRoot) {
  ReactDOM.render(<CaptureConsent />, captureConsentRoot);
}

// Mount DoorLockConfig in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const doorLockConfigRoot = document.getElementById('door-lock-config-root');
if (doorLockConfigRoot) {
  ReactDOM.render(<DoorLockConfig />, doorLockConfigRoot);
}

// Mount DungeonLootConfig in its own DOM node - same reasoning as DoorLockConfig.
const dungeonLootConfigRoot = document.getElementById('dungeon-loot-config-root');
if (dungeonLootConfigRoot) {
  ReactDOM.render(<DungeonLootConfig />, dungeonLootConfigRoot);
}

// Mount DungeonManager in its own DOM node - same reasoning as DoorLockConfig.
const dungeonManagerRoot = document.getElementById('dungeon-manager-root');
if (dungeonManagerRoot) {
  ReactDOM.render(<DungeonManager />, dungeonManagerRoot);
}

// Mount DungeonSearchProgress in its own DOM node (always active, manages
// its own visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const dungeonSearchRoot = document.getElementById('dungeon-search-root');
if (dungeonSearchRoot) {
  ReactDOM.render(<DungeonSearchProgress />, dungeonSearchRoot);
}

// Mount WildlifeZoneManager in its own DOM node - same reasoning as DoorLockConfig.
const wildlifeZoneManagerRoot = document.getElementById('wildlife-zone-manager-root');
if (wildlifeZoneManagerRoot) {
  ReactDOM.render(<WildlifeZoneManager />, wildlifeZoneManagerRoot);
}

// Mount ZonePreviewOverlay in its own DOM node (always active, manages its
// own visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const zonePreviewOverlayRoot = document.getElementById('zone-preview-overlay-root');
if (zonePreviewOverlayRoot) {
  ReactDOM.render(<ZonePreviewOverlay />, zonePreviewOverlayRoot);
}

// Mount ZoneTypePicker in its own DOM node - shown only at the end of a zone
// capture, when the server asks what the outline is for.
const zoneTypePickerRoot = document.getElementById('zone-type-picker-root');
if (zoneTypePickerRoot) {
  ReactDOM.render(<ZoneTypePicker />, zoneTypePickerRoot);
}

// Mount HouseAdminConfig/HouseBuyPrompt in their own DOM nodes - same
// reasoning as DoorLockConfig.
const houseAdminConfigRoot = document.getElementById('house-admin-config-root');
if (houseAdminConfigRoot) {
  ReactDOM.render(<HouseAdminConfig />, houseAdminConfigRoot);
}
const houseBuyPromptRoot = document.getElementById('house-buy-prompt-root');
if (houseBuyPromptRoot) {
  ReactDOM.render(<HouseBuyPrompt />, houseBuyPromptRoot);
}
const housingManageRoot = document.getElementById('housing-manage-root');
if (housingManageRoot) {
  ReactDOM.render(<HousingManage />, housingManageRoot);
}
const homeDecorRoot = document.getElementById('home-decor-root');
if (homeDecorRoot) {
  ReactDOM.render(<HomeDecor />, homeDecorRoot);
}

// Mount HorseStable in its own DOM node (always active, manages its own
// visibility) - same reasoning as DoorLockConfig.
const horseStableRoot = document.getElementById('horse-stable-root');
if (horseStableRoot) {
  ReactDOM.render(<HorseStable />, horseStableRoot);
}

// Mount DoorCodePrompt in its own DOM node (always active, manages its own
// visibility) - same reasoning as the other overlays.
const doorCodePromptRoot = document.getElementById('door-code-prompt-root');
if (doorCodePromptRoot) {
  ReactDOM.render(<DoorCodePrompt />, doorCodePromptRoot);
}

// Mount DoorLocksList in its own DOM node (always active, manages its own
// visibility) - same reasoning as the other overlays.
const doorLocksListRoot = document.getElementById('door-locks-list-root');
if (doorLocksListRoot) {
  ReactDOM.render(<DoorLocksList />, doorLocksListRoot);
}

// Mount AfkChallenge in its own DOM node (always active, manages its own
// visibility) - same reasoning as the other overlays.
const afkChallengeRoot = document.getElementById('afk-challenge-root');
if (afkChallengeRoot) {
  ReactDOM.render(<AfkChallenge />, afkChallengeRoot);
}

// Mount FinePrompt in its own DOM node (always active, manages its own
// visibility) - same reasoning as the other overlays.
const finePromptRoot = document.getElementById('fine-prompt-root');
if (finePromptRoot) {
  ReactDOM.render(<FinePrompt />, finePromptRoot);
}

// Mount FineResponse in its own DOM node (always active, manages its own
// visibility) - same reasoning as the other overlays. Target-side response
// to justiceSystem.ts's asynchronous fine flow (see FinePrompt above for the
// issuing guard's own, separate UI).
const fineResponseRoot = document.getElementById('fine-response-root');
if (fineResponseRoot) {
  ReactDOM.render(<FineResponse />, fineResponseRoot);
}

// Mount Mailbox in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const mailboxRoot = document.getElementById('mailbox-root');
if (mailboxRoot) {
  ReactDOM.render(<Mailbox />, mailboxRoot);
}

// Mount the notice board (E at a board, "/notes") in its own DOM node, manages its own visibility.
const noticeBoardRoot = document.getElementById('noticeboard-root');
if (noticeBoardRoot) {
  ReactDOM.render(<NoticeBoard />, noticeBoardRoot);
}

// Mount PublicWork ("/travail") in its own DOM node, manages its own visibility.
const publicWorkRoot = document.getElementById('public-work-root');
if (publicWorkRoot) {
  ReactDOM.render(<PublicWork />, publicWorkRoot);
}

// Mount Depot (regional depot panel) in its own DOM node (always active,
// manages its own visibility) - same reasoning as Mailbox.
const depotRoot = document.getElementById('depot-root');
if (depotRoot) {
  ReactDOM.render(<Depot />, depotRoot);
}

// Mount Contracts in its own DOM node (always active, manages its own
// visibility) - same reasoning as Mailbox above.
const contractsRoot = document.getElementById('contracts-root');
if (contractsRoot) {
  ReactDOM.render(<Contracts />, contractsRoot);
}

// Mount PapersMenu/PapersShow in their own DOM nodes (always active, manage
// their own visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const papersRoot = document.getElementById('papers-root');
if (papersRoot) {
  ReactDOM.render(<PapersMenu />, papersRoot);
}
const papersShowRoot = document.getElementById('papers-show-root');
if (papersShowRoot) {
  ReactDOM.render(<PapersShow />, papersShowRoot);
}

// Mount ItemGiver in its own DOM node (always active, manages its own
// visibility) - same reasoning as CitySelection/TargetMenu/EmoteWheel.
const itemGiverRoot = document.getElementById('item-giver-root');
if (itemGiverRoot) {
  ReactDOM.render(<ItemGiver />, itemGiverRoot);
}

// Dev preview: auto-show city selection with mock data when not in game
if (DEV_PREVIEW) {
  mountDevPreviewPanel();

  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('skymp5-client:showCitySelection', {
      detail: {
        cities: [
          { id: 'whiterun',   name: 'Blancherive', hold: 'Blancherive',  description: 'Au cœur de Bordeciel, Blancherive veille sous l\'œil de Fort-Dragon, le palais du jarl. Terre de commerce et de tradition, c\'est ici que bat le cœur de la province.', blason: 'emblem_whiterun'},
          { id: 'solitude',   name: 'Solitude',    hold: 'Haafingar',    description: 'Capitale de Bordeciel perchée sur sa grande arche de pierre, à l\'embouchure de la Karth. Fidèle à l\'Empire, Solitude rayonne d\'un prestige politique et culturel sans égal.' },
          { id: 'windhelm',   name: 'Vendeaume',   hold: 'Estemarche',   description: 'Fondée par Ysgramor, Vendeaume est la plus ancienne cité de Bordeciel. Pierre noire et givre, palais des Rois : l\'honneur du vieux Nord y règne depuis des millénaires.' },
          { id: 'riften',     name: 'Faillaise',   hold: 'La Brèche',    description: 'Bâtie sur les canaux du lac Honrich, Faillaise est une ville de bois et d\'eau où marchands et Guilde des voleurs se disputent l\'influence sur les quais.' },
          { id: 'markarth',   name: 'Markarth',    hold: 'La Crevasse',  description: 'Bâtie dans les ruines dwemer de Nchuand-Zel, Markarth est une cité de pierre taillée à flanc de montagne, hantée par de sombres secrets et les rancœurs des Parias.' },
          { id: 'morthal',    name: 'Morthal',     hold: 'Hjaalmarche',  description: 'Bourgade brumeuse perdue au milieu des marais du Drajkmyr, Morthal vit à l\'écart du monde. Ses habitants murmurent d\'étranges histoires au coin du feu.' },
          { id: 'dawnstar',   name: 'Aubétoile',   hold: 'Le Clos',      description: 'Port glacé sur la Mer des Fantômes, Aubétoile est une ville de mineurs et de marins endurcis par les rigueurs arctiques du Clos.' },
          { id: 'winterhold', name: 'Fortdhiver',  hold: 'Fortdhiver',   description: 'Autrefois grande cité prospère, Fortdhiver n\'est plus que l\'ombre d\'elle-même depuis le Grand Effondrement. Seul le Collège des mages résiste encore.' },
          { id: 'falkreath',  name: 'Épervine',    hold: 'Épervine',     description: 'Bercée par les forêts de pins du sud, Épervine est réputée pour son immense cimetière et son lien profond avec la mort et le cycle des saisons.' },
        ]
      }
    }));
  }, 100);

  // Dev preview: auto-show the door lock config panel with mock data
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('skymp5-client:showDoorLockConfig', {
      detail: {
        refrId: 12345,
        factions: [
          { id: 'thalmor', name: 'Thalmor', ranks: [
            { id: 0, name: 'Recrue' },
            { id: 1, name: 'Officier' },
            { id: 2, name: 'Chef' },
          ] },
        ],
        identities: [
          { actorId: 101, name: 'Elenwen', online: true },
          { actorId: 102, name: 'Rulindil', online: true },
          { actorId: 103, name: 'Ondolemar', online: false },
        ],
        current: null,
      }
    }));
  }, 150);
}

// Open/close chat input on browser focus
window.addEventListener('skymp5-client:browserFocused', () => {
  window.storage.dispatch({ type: 'UPDATE_CHAT_SHOWINPUT', data: true });
});
window.addEventListener('skymp5-client:browserUnfocused', () => {
  window.storage.dispatch({ type: 'UPDATE_CHAT_SHOWINPUT', data: false });
});
